<template>
  <!-- EcountEtl 실행 상세 (SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-RUNS-006~010) -->
  <div class="page-container-v2">
    <div class="page-header">
      <a-button type="link" style="padding-left: 0;" @click="goBack">
        ← {{ $t('ecountEtl_runs_title') }}
      </a-button>
      <span class="page-title" style="margin-left: 8px;">{{ $t('ecountEtl_runDetail_title') }} #{{ runId }}</span>
      <div style="display: flex; align-items: center; gap: 8px; margin-left: auto;">
        <timezone-toggle :current-tz="currentTz" @toggle="toggleTz" />
        <!-- running 상태일 때만 폴링 안내 -->
        <a-badge v-if="isRunning" status="processing" :text="$t('ecountEtl_runDetail_polling')" />
        <a-button size="small" type="text" :loading="loading" @click="fetchDetail">
          <reload-outlined />
        </a-button>
      </div>
    </div>

    <!-- 오류 -->
    <a-alert
      v-if="error"
      type="error"
      :message="error"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="error = null"
    />

    <!-- 실행 메타 정보 -->
    <a-card :loading="loading && !runMeta.runId" style="margin-bottom: 16px;">
      <a-descriptions :column="{ xs: 1, sm: 2, lg: 3 }" bordered size="small">
        <a-descriptions-item :label="$t('ecountEtl_runDetail_meta_runId')">
          {{ runMeta.runId ?? '-' }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('ecountEtl_runDetail_meta_jobType')">
          {{ runMeta.jobType ?? '-' }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('ecountEtl_runDetail_meta_status')">
          <run-status-badge v-if="runMeta.status" :value="runMeta.status" />
          <span v-else>-</span>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('ecountEtl_runDetail_meta_startedAt')">
          {{ runMeta.startedAt ? formatTime(runMeta.startedAt) : '-' }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('ecountEtl_runDetail_meta_finishedAt')">
          {{ runMeta.finishedAt ? formatTime(runMeta.finishedAt) : '-' }}
        </a-descriptions-item>
        <a-descriptions-item label="Fetched / Inserted / Updated / Failed">
          {{ runMeta.fetchedRows ?? '-' }} /
          {{ runMeta.inserted ?? '-' }} /
          {{ runMeta.updated ?? '-' }} /
          {{ runMeta.failed ?? '-' }}
        </a-descriptions-item>
      </a-descriptions>
    </a-card>

    <!-- 데드레터 목록 -->
    <a-card :loading="loading && deadLetters.length === 0">
      <template #title>
        {{ $t('ecountEtl_runDetail_deadLetters') }}
        <a-badge :count="deadLetters.length" :overflow-count="9999" style="margin-left: 8px;" />
      </template>
      <template #extra>
        <diagnostic-package-copy
          :run-id="runId"
          :items="deadLetters"
        />
      </template>
      <a-table
        :data-source="deadLetters"
        :columns="deadLetterColumns"
        :pagination="deadLetterPagination"
        row-key="id"
        size="small"
        :scroll="{ x: 700 }"
        @change="onDeadLetterPageChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'createdAt'">
            {{ formatTime(record.createdAt) }}
          </template>
          <template v-if="column.key === 'actions'">
            <a-button size="small" type="link" @click="openRawModal(record)">
              {{ $t('ecountEtl_deadLetters_viewRaw') }}
            </a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- Raw JSON 모달 -->
    <a-modal
      :open="rawModalVisible"
      :title="$t('ecountEtl_deadLetters_rawModal_title')"
      :footer="null"
      :width="700"
      @cancel="rawModalVisible = false"
    >
      <pre style="max-height: 500px; overflow: auto; font-size: 12px;">{{ rawJson }}</pre>
    </a-modal>
  </div>
</template>

<script>
// @MX:SPEC: SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-RUNS-006~010
// @MX:WARN: [AUTO] running 상태 폴링은 5초 인터벌 — 해제 누락 시 메모리 누수
// @MX:REASON: [AUTO] 컴포넌트 언마운트 시 clearInterval 호출 필수 (onBeforeUnmount)

import { ReloadOutlined } from '@ant-design/icons-vue';
import { useKstTime } from './composables/useKstTime.js';
import { getRunDetail, extractApiError, parsePaginatedResponse } from '../../apis/ecountEtl.js';
import RunStatusBadge from './_components/RunStatusBadge.vue';
import DiagnosticPackageCopy from './_components/DiagnosticPackageCopy.vue';
import TimezoneToggle from './_components/TimezoneToggle.vue';
import { RouterEnum } from '../../enums/router.enums.js';

const POLL_INTERVAL_MS = 5000;

export default {
  name: 'EcountEtlRunDetail',
  components: { ReloadOutlined, RunStatusBadge, DiagnosticPackageCopy, TimezoneToggle },

  setup() {
    const { currentTz, toggleTz, format } = useKstTime();
    return { currentTz, toggleTz, format };
  },

  data() {
    return {
      loading: false,
      error: null,
      runMeta: {},
      deadLetters: [],
      deadLetterPagination: { current: 1, pageSize: 20, total: 0 },
      rawModalVisible: false,
      rawJson: '',
      pollingTimer: null,
    };
  },

  computed: {
    runId() {
      return this.$route.params.runId;
    },
    isRunning() {
      return this.runMeta.status === 'running';
    },
    deadLetterColumns() {
      return [
        { key: 'id', dataIndex: 'id', title: this.$t('ecountEtl_deadLetters_col_id'), width: 80 },
        { key: 'reason', dataIndex: 'reason', title: this.$t('ecountEtl_deadLetters_col_reason') },
        { key: 'retryCount', dataIndex: 'retryCount', title: this.$t('ecountEtl_deadLetters_col_retryCount'), width: 100 },
        { key: 'createdAt', dataIndex: 'createdAt', title: this.$t('ecountEtl_deadLetters_col_createdAt') },
        { key: 'actions', title: this.$t('ecountEtl_deadLetters_col_actions'), width: 100 },
      ];
    },
  },

  watch: {
    isRunning(val) {
      // running 상태 진입 시 폴링 시작, 종료 시 폴링 중지
      if (val) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
  },

  mounted() {
    this.fetchDetail();
  },

  beforeUnmount() {
    // @MX:WARN 대응: 메모리 누수 방지
    this.stopPolling();
  },

  methods: {
    async fetchDetail() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          page: this.deadLetterPagination.current,
          limit: this.deadLetterPagination.pageSize,
        };
        const res = await getRunDetail(this.runId, params);
        const data = res.data ?? {};
        this.runMeta = data.run ?? data;
        const dlParsed = parsePaginatedResponse({ data: data.deadLetters ?? { items: [], total: 0 } });
        this.deadLetters = dlParsed.items;
        this.deadLetterPagination.total = dlParsed.total;
      } catch (e) {
        const err = extractApiError(e);
        const localized = this.$t(err.messageKey, err.messageParams);
        this.error = localized.startsWith('ecountEtl_') ? err.message : localized;
      } finally {
        this.loading = false;
      }
    },
    onDeadLetterPageChange(pag) {
      this.deadLetterPagination.current = pag.current;
      this.fetchDetail();
    },
    startPolling() {
      if (this.pollingTimer) return;
      this.pollingTimer = setInterval(() => {
        this.fetchDetail();
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    openRawModal(record) {
      this.rawJson = JSON.stringify(record, null, 2);
      this.rawModalVisible = true;
    },
    formatTime(value) {
      return this.format(value);
    },
    goBack() {
      this.$router.push({ name: RouterEnum.EcountEtl.Runs.name });
    },
  },
};
</script>
